import React from 'react';
import {
  AreaChart, Area, BarChart, Bar, LineChart, Line, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
} from 'recharts';
import StatCard from '../components/StatCard';
import ChartCard from '../components/ChartCard';
import LoadingSpinner from '../components/LoadingSpinner';
import { useAnalyticsData } from '../hooks/useAnalyticsData';
import { PeriodConfig, COLORS, formatDate, formatMonth, formatNumber, tooltipStyle } from '../types';

interface VideosTabProps {
  period: PeriodConfig;
}

const ENDPOINTS = [
  'videos-overview', 'videos-daily', 'videos-monthly',
  'videos-hourly-5d', 'video-status-breakdown',
  'creators-daily', 'creators-monthly',
];

const VideosTab: React.FC<VideosTabProps> = ({ period }) => {
  const { data, loading } = useAnalyticsData(ENDPOINTS, period);

  if (loading) return <LoadingSpinner />;

  const videosOverview = data['videos-overview'];
  const dailyVideos = (data['videos-daily'] || []).map((d: any) => ({ ...d, date: formatDate(d.date) }));
  const monthlyVideos = (data['videos-monthly'] || []).map((d: any) => ({ ...d, label: formatMonth(d.year, d.month) }));
  const hourly5d = (data['videos-hourly-5d'] || []).map((d: any) => ({ ...d, label: `${d.date.slice(5)} ${d.hour}:00` }));
  const statusBreakdown = data['video-status-breakdown'] || [];
  const creatorsDaily = (data['creators-daily'] || []).map((d: any) => ({ ...d, date: formatDate(d.date) }));
  const creatorsMonthly = data['creators-monthly'] || [];

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard title="Total Active Videos" value={videosOverview?.totalVideos || 0} />
        <StatCard title="Today" value={videosOverview?.todayVideos || 0} />
        <StatCard title="This Week" value={videosOverview?.weekVideos || 0} />
        <StatCard title="This Month" value={videosOverview?.monthVideos || 0} />
      </div>

      <ChartCard title="Video Status Breakdown">
        <div className="flex flex-col lg:flex-row items-center gap-6">
          <ResponsiveContainer width="50%" height={250}>
            <PieChart>
              <Pie data={statusBreakdown} dataKey="count" nameKey="status" cx="50%" cy="50%" outerRadius={80} innerRadius={40} label={({ name, percent }: any) => `${name} ${(percent * 100).toFixed(0)}%`}>
                {statusBreakdown.map((_: any, i: number) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
              </Pie>
              <Tooltip contentStyle={tooltipStyle} />
            </PieChart>
          </ResponsiveContainer>
          <div className="space-y-2">
            {statusBreakdown.map((s: any, i: number) => (
              <div key={i} className="flex items-center gap-3">
                <div className="w-3 h-3 rounded-full" style={{ backgroundColor: COLORS[i % COLORS.length] }} />
                <span className="text-sm text-gray-300">{s.status}</span>
                <span className="text-sm text-white font-medium">{formatNumber(s.count)}</span>
              </div>
            ))}
          </div>
        </div>
      </ChartCard>

      <ChartCard title="Daily Video Uploads (Last 90 Days)">
        <ResponsiveContainer width="100%" height={300}>
          <AreaChart data={dailyVideos}>
            <defs>
              <linearGradient id="videoGrad" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#F42026" stopOpacity={0.3} />
                <stop offset="95%" stopColor="#F42026" stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
            <XAxis dataKey="date" stroke="#64748b" fontSize={11} tickLine={false} interval="preserveStartEnd" />
            <YAxis stroke="#64748b" fontSize={11} tickLine={false} axisLine={false} />
            <Tooltip contentStyle={tooltipStyle} />
            <Area type="monotone" dataKey="count" stroke="#F42026" fill="url(#videoGrad)" strokeWidth={2} />
          </AreaChart>
        </ResponsiveContainer>
      </ChartCard>

      <ChartCard title="Video Uploads Hourly (Last 5 Days)">
        <ResponsiveContainer width="100%" height={300}>
          <BarChart data={hourly5d}>
            <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
            <XAxis dataKey="label" stroke="#64748b" fontSize={9} tickLine={false} interval="preserveStartEnd" />
            <YAxis stroke="#64748b" fontSize={11} tickLine={false} axisLine={false} />
            <Tooltip contentStyle={tooltipStyle} />
            <Bar dataKey="count" fill="#F42026" radius={[2, 2, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </ChartCard>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <ChartCard title="Monthly Videos (from Jun 2025)">
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={monthlyVideos}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
              <XAxis dataKey="label" stroke="#64748b" fontSize={11} tickLine={false} />
              <YAxis stroke="#64748b" fontSize={11} tickLine={false} axisLine={false} />
              <Tooltip contentStyle={tooltipStyle} />
              <Bar dataKey="count" fill="#F42026" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="Creators Monthly (from Jun 2025)">
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={creatorsMonthly}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
              <XAxis dataKey="month" stroke="#64748b" fontSize={11} tickLine={false} />
              <YAxis stroke="#64748b" fontSize={11} tickLine={false} axisLine={false} />
              <Tooltip contentStyle={tooltipStyle} />
              <Bar dataKey="creatorCount" fill="#FF9C15" name="Creators" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>

      <ChartCard title="Daily Creators & Videos (Last 30 Days)">
        <ResponsiveContainer width="100%" height={350}>
          <LineChart data={creatorsDaily}>
            <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
            <XAxis dataKey="date" stroke="#64748b" fontSize={11} tickLine={false} interval="preserveStartEnd" />
            <YAxis stroke="#64748b" fontSize={11} tickLine={false} axisLine={false} />
            <Tooltip contentStyle={tooltipStyle} />
            <Legend />
            <Line type="monotone" dataKey="creatorCount" name="Unique Creators" stroke="#FF9C15" strokeWidth={2} dot={false} />
            <Line type="monotone" dataKey="videoCount" name="Videos" stroke="#F42026" strokeWidth={2} dot={false} />
          </LineChart>
        </ResponsiveContainer>
      </ChartCard>
    </div>
  );
};

export default VideosTab;
